import type { RevisionAttributionSummary } from "./revision-attribution.js";
import type { GpcVitalsAccessStatus } from "./project.js";

// ======================================================================================|
// MARK: Internal Account Detection

/**
 * Returns true if the given email belongs to an internal Apptonomy account.
 * Used to suppress analytics tracking for internal users.
 */
export function isInternalAnalyticsEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.toLowerCase().endsWith("@apptonomy.ai");
}

/**
 * True when the email is a Playwright e2e-generated account
 * (A7Y-3634). These are internal addresses that must be FLAGGED as
 * test traffic, never suppressed — the nightly e2e signup is the
 * probe that proves the onboarding funnel joins.
 * Patterns: a7y-webapp/tests/e2e/direct-signup.spec.ts (TEST_EMAIL,
 * REAP_ALLOWLIST_PREFIXES).
 */
export function isE2eTestTrafficEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  if (!lower.endsWith("@apptonomy.ai")) return false;
  return lower.startsWith("automated_testing+") || lower.startsWith("e2e-waitlist-");
}

// ======================================================================================|
// MARK: Platform & Core Types

/** Analytics platform identifier */
export type AnalyticsPlatform = "asc" | "gpc";

/** Date range for analytics queries */
export interface AnalyticsDateRange {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

/** Single KPI with value and delta vs previous period */
export interface AnalyticsKpi {
  value: number;
  delta: number;
}

/** Single time-series data point */
export interface AnalyticsTimeSeries {
  date: string;
  value: number;
}

// ======================================================================================|
// MARK: GPC Types (shared between ingestion & query)

/** GPC report categories available via GCS CSV export */
export type GpcReportCategory =
  | "INSTALLS"
  | "RATINGS"
  | "STORE_PERFORMANCE"
  | "REVIEWS"
  | "PROMOTIONAL_CONTENT"
  | "CANCELLATION_REASONS"
  | "SUBSCRIPTIONS"
  | "EARNINGS"
  | "ESTIMATED_SALES";

/** GPC report dimension (determines which CSV variant to download) */
export type GpcReportDimension =
  | "overview"
  | "country"
  | "os_version"
  | "device"
  | "app_version"
  | "carrier"
  | "language"
  | "traffic_source"
  | "search_term"
  | "utm_source";

/** ASC analytics report categories */
export type AscAnalyticsReportCategory =
  | "APP_USAGE"
  | "APP_STORE_ENGAGEMENT"
  | "COMMERCE"
  | "PERFORMANCE";

// ======================================================================================|
// MARK: Analytics Target

/** User-configured metric target stored in Firestore */
export interface AnalyticsTarget {
  id: string;
  metricKey: string; // e.g., "asc.totalDownloads", "gpc.totalInstalls"
  platform: AnalyticsPlatform;
  value: number;
  label?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

/** Request body for creating/updating a target */
export interface SetAnalyticsTargetRequest {
  metricKey: string;
  platform: AnalyticsPlatform;
  value: number;
  label?: string;
}

// ======================================================================================|
// MARK: API Response Types

/** GET /analytics/overview */
export interface AnalyticsOverviewResponse {
  dateRange: AnalyticsDateRange;
  comparisonRange: AnalyticsDateRange;
  asc: {
    visitors: number;
    visitorsDelta: number;
    downloads: number;
    downloadsDelta: number;
    impressions: number;
    impressionsDelta: number;
    conversionRate: number;
    conversionRateDelta: number;
  } | null;
  gpc: {
    visitors: number;
    visitorsDelta: number;
    acquisitions: number;
    acquisitionsDelta: number;
    conversionRate: number;
    conversionRateDelta: number;
  } | null;
}

/** GET /analytics/timeseries */
export interface AnalyticsTimeseriesResponse {
  dateRange: AnalyticsDateRange;
  asc?: {
    series: Array<Record<string, number | string>>;
    previousSeries?: Array<Record<string, number | string>>;
  };
  gpc?: {
    series: Array<Record<string, number | string>>;
    previousSeries?: Array<Record<string, number | string>>;
  };
}

/** GET /analytics/detail/asc */
export interface AscDetailResponse {
  dateRange: AnalyticsDateRange;
  comparisonRange: AnalyticsDateRange;
  revenue: {
    proceeds: AnalyticsKpi;
    comparisonLabel: string;
    currency?: string;
  };
  proceedsByProduct: Array<{
    productName: string;
    proceeds: number;
    currency?: string;
  }>;
  downloads: {
    firstTimeDownloads: AnalyticsKpi;
    redownloads: AnalyticsKpi;
    activeDevices: AnalyticsKpi;
  };
  /**
   * App usage from `App Sessions Standard` (A7Y-5911). `sessions` is the
   * range total of the same summed series the Sessions chart plots.
   */
  usage: {
    sessions: AnalyticsKpi;
  };
  storePerformance: {
    impressions: AnalyticsKpi;
    productPageViews: AnalyticsKpi;
    conversionRate: AnalyticsKpi;
  };
  hygieneMetrics: {
    reviews: AnalyticsKpi;
    averageRating: AnalyticsKpi;
  };
  reviewsAndRatings: {
    totalReviews: number;
    totalReviewsDelta: number;
    averageRating: number;
    averageRatingDelta: number;
    ratingDistribution: Array<{
      date: string;
      stars: { 1: number; 2: number; 3: number; 4: number; 5: number };
    }>;
    /**
     * Per-day average rating timeseries derived from the same review rows that
     * back ratingDistribution. Days with zero reviews are omitted (matching
     * the existing ratingDistribution / reviewsSparkline behaviour). A7Y-477.
     */
    averageRatingTimeseries: AnalyticsTimeSeries[];
  };
  downloadsBySource: Array<{
    sourceType: string;
    totalDownloads: number;
    delta: number;
  }>;
  totalDownloadsTimeseries: {
    current: AnalyticsTimeSeries[];
    previous: AnalyticsTimeSeries[];
  };
  sparklines: {
    firstTimeDownloads: AnalyticsTimeSeries[];
    redownloads: AnalyticsTimeSeries[];
    // No activeDevices series (A7Y-5801, decision D2): summing unique_devices
    // across dimension rows overcounts, so ASC active devices is a KPI only.
    sessions: AnalyticsTimeSeries[];
    impressions: AnalyticsTimeSeries[];
    productPageViews: AnalyticsTimeSeries[];
    conversionRate: AnalyticsTimeSeries[];
    reviews: AnalyticsTimeSeries[];
  };
  /**
   * Published-revision annotation markers (A7Y-476). Folded into the detail
   * response so the frontend no longer needs a separate /annotations round-trip.
   */
  annotations: AnalyticsAnnotation[];
}

/** GET /analytics/detail/gpc */
export interface GpcDetailResponse {
  dateRange: AnalyticsDateRange;
  comparisonRange: AnalyticsDateRange;
  earnings: {
    totalEarnings: number;
    totalEarningsDelta: number;
    comparisonLabel: string;
  };
  earningsByProduct: Array<{
    productName: string;
    earnings: number;
  }>;
  acquisition: {
    installEvents: AnalyticsKpi;
    uninstallEvents: AnalyticsKpi;
    activeDevices: AnalyticsKpi;
  };
  /**
   * Store-listing KPIs from Play Console's `store_performance` report. `null`
   * when the window holds no `store_performance` rows for the app — no report
   * was delivered (the store link has no reporting bucket, or none has been
   * imported yet) — so a consumer never shows an absent report as zero
   * visitors (A7Y-5808).
   */
  storePerformance: {
    storeListingVisitors: AnalyticsKpi;
    storeListingConvRate: AnalyticsKpi;
    storeListingAcquisitions: AnalyticsKpi;
  } | null;
  hygieneMetrics: {
    ratings: AnalyticsKpi;
    averageRating: AnalyticsKpi;
  };
  reviewsAndRatings: {
    totalRatings: number;
    totalRatingsDelta: number;
    averageRating: number;
    averageRatingDelta: number;
    ratingDistribution: Array<{
      date: string;
      stars: { 1: number; 2: number; 3: number; 4: number; 5: number };
    }>;
    /**
     * Per-day average rating timeseries derived from the same review rows that
     * back ratingDistribution. Days with zero reviews are omitted (matching
     * the existing ratingDistribution / reviewsSparkline behaviour). A7Y-477.
     */
    averageRatingTimeseries: AnalyticsTimeSeries[];
  };
  installsByChannel: {
    organicInstalls: AnalyticsKpi;
    totalInstalls: AnalyticsKpi;
  };
  installsBySource: Array<{
    sourceType: string;
    totalInstalls: number;
    delta: number;
  }>;
  totalInstallsTimeseries: {
    current: AnalyticsTimeSeries[];
    previous: AnalyticsTimeSeries[];
  };
  sparklines: {
    installEvents: AnalyticsTimeSeries[];
    uninstallEvents: AnalyticsTimeSeries[];
    activeDevices: AnalyticsTimeSeries[];
    storeListingVisitors: AnalyticsTimeSeries[];
    storeListingConvRate: AnalyticsTimeSeries[];
    storeListingAcquisitions: AnalyticsTimeSeries[];
    ratings: AnalyticsTimeSeries[];
  };
  /**
   * Published-revision annotation markers (A7Y-476). Folded into the detail
   * response so the frontend no longer needs a separate /annotations round-trip.
   */
  annotations: AnalyticsAnnotation[];
}

/** GET /analytics/targets */
export interface AnalyticsTargetsResponse {
  targets: AnalyticsTarget[];
}

/** GET /analytics/drilldown */
export interface AnalyticsDrilldownResponse {
  dateRange: AnalyticsDateRange;
  comparisonRange: AnalyticsDateRange;
  platform: AnalyticsPlatform;
  report: string;
  dimension: string;
  breakdown: Array<{
    dimensionValue: string;
    value: number;
    delta: number;
    /**
     * Human-readable label for the row (A7Y-1815). When omitted, consumers fall
     * back to `dimensionValue`. Used by the GPC organic/branded drilldown to
     * render display copy (e.g. "Google Play explore (organic)") distinct from
     * the raw dimension value.
     */
    label?: string;
    /**
     * Traffic-source classification for the GPC organic view (A7Y-1815):
     * `organic` (Google Play explore/search), `referral` (Ads and referrals),
     * or `other` (everything else, incl. the "Other" bucket). Absent on
     * non-GPC-organic drilldowns.
     */
    group?: "organic" | "referral" | "other";
    /**
     * Whether this row's underlying data is available for the requested split
     * (A7Y-1815). `false` flags an honestly-degraded row (e.g. branded search
     * with no brand token to match against); `reason` explains why.
     */
    available?: boolean;
    /** When `available` is false (or low-confidence), why (A7Y-1815). */
    reason?: string;
  }>;
  timeseries?: Array<{
    date: string;
    values: Record<string, number>;
  }>;
  /**
   * Data provenance for per-event (page_title/page_type) drilldowns (A7Y-2880).
   * "detailed": rows come from the Detailed report tier (privacy-thresholded:
   * rows under 5 users/devices are suppressed by Apple and values are noised).
   * "app_level_fallback": no attributable detailed rows existed in the window;
   * `appLevel` carries the Standard-report app-level total for the same metric
   * so the UI can correlate the event window and say so.
   * Absent on non-per-event dimensions.
   */
  source?: "detailed" | "app_level_fallback";
  appLevel?: { value: number; delta: number };
}

/**
 * One month of one country (or of all countries) in the Google Play
 * month-by-country table (A7Y-4670). Every figure comes from Play's store
 * performance report by country: `installs` is store listing acquisitions and
 * `conversionRate` is installs over visitors for the whole month, never an
 * average of daily rates.
 */
export interface GpcMonthlyCountryCell {
  /** "YYYY-MM" */
  month: string;
  visitors: number;
  installs: number;
  /** Ratio 0..1; null when the month had no visitors. */
  conversionRate: number | null;
  /**
   * Change from the month before, in percent. Null unless both months are
   * fully reported and the month before had a non-zero value.
   */
  visitorsChange: number | null;
  /** As `visitorsChange`, for installs. */
  installsChange: number | null;
  /** Change from the month before in percentage points; null on the same terms. */
  conversionRateChange: number | null;
}

/** How much of a month Play has reported for the app (A7Y-4670). */
export interface GpcMonthlyCountryMonth {
  /** "YYYY-MM" */
  month: string;
  /** Latest day (YYYY-MM-DD) with store performance rows in the month; null when none. */
  lastReportedDate: string | null;
  /**
   * True when the month's last day is reported and the plan's data-retention
   * window does not cut the start of the month.
   */
  complete: boolean;
}

/** GET /analytics/monthly-by-country (A7Y-4670) */
export interface GpcMonthlyCountryResponse {
  platform: "gpc";
  /** "YYYY-MM" */
  startMonth: string;
  /** "YYYY-MM" */
  endMonth: string;
  /** The requested months, oldest first. */
  months: GpcMonthlyCountryMonth[];
  /** All countries summed, one cell per requested month. */
  total: GpcMonthlyCountryCell[];
  /**
   * One row per country Play reports, ordered by installs over the requested
   * months (descending) with Play's "Other" bucket last. `country` is the
   * value Play exports: an ISO 3166-1 alpha-2 code, or "Other".
   */
  countries: Array<{ country: string; cells: GpcMonthlyCountryCell[] }>;
}

/**
 * Google's bad-behaviour thresholds for Android vitals (A7Y-5742): an app at
 * or above either rate may be less discoverable on Google Play. Fractions of
 * users, not percentages.
 * source: Play Console Help, "Android vitals" bad behavior thresholds, as quoted in A7Y-5742.
 */
export const ANDROID_VITALS_BAD_BEHAVIOR_THRESHOLDS = {
  userPerceivedAnrRate: 0.0047,
  userPerceivedCrashRate: 0.0109,
} as const;

/**
 * The stored shape of Android vitals rows (A7Y-5742): a7y-functions writes
 * them to the GPC `vitals` table under these report names and `data` keys,
 * and a7y-api reads them back by the same names.
 */
export const GPC_VITALS_STORAGE = {
  table: "vitals",
  /** Days of history each daily ingestion run re-reads from Google. */
  lookbackDays: 30,
  versionKey: "version_code",
  usersKey: "distinct_users",
  anr: { reportName: "vitals_anr_rate_version", rateKey: "user_perceived_anr_rate" },
  crash: { reportName: "vitals_crash_rate_version", rateKey: "user_perceived_crash_rate" },
} as const;

/** One app version's vitals over the requested range (A7Y-5742). */
export interface GpcVitalsVersionRow {
  /** Play `versionCode`, as Google reports it. */
  versionCode: string;
  /** Daily distinct users summed over the range (the rates' weight). */
  distinctUsers: number;
  /** User-perceived ANR rate, a fraction weighted by daily users; null when unreported. */
  anrRate: number | null;
  /** User-perceived crash rate, a fraction weighted by daily users; null when unreported. */
  crashRate: number | null;
  /** True when `anrRate` is at or above Google's threshold. */
  anrOverThreshold: boolean;
  /** True when `crashRate` is at or above Google's threshold. */
  crashOverThreshold: boolean;
  /** Days in the range with a reported row for this version. */
  days: number;
}

/** GET /analytics/vitals (A7Y-5742) */
export interface GpcVitalsResponse {
  platform: "gpc";
  /** "YYYY-MM-DD" */
  startDate: string;
  /** "YYYY-MM-DD" */
  endDate: string;
  thresholds: typeof ANDROID_VITALS_BAD_BEHAVIOR_THRESHOLDS;
  /**
   * What the Reporting API answered on the last run; null before the first
   * run that read vitals for this project.
   */
  access: { status: GpcVitalsAccessStatus; checkedAt: number; detail?: string } | null;
  /**
   * First day with a reported row in the range, or null when none. Ingestion
   * re-reads `GPC_VITALS_STORAGE.lookbackDays` on every run, so a range that starts earlier than
   * the first ingested day has rows only from this day on.
   */
  firstReportedDate: string | null;
  /** Last day with a reported row in the range, or null when none. */
  lastReportedDate: string | null;
  /** All versions together, weighted the same way. */
  overall: Omit<GpcVitalsVersionRow, "versionCode" | "days">;
  /** Ordered by distinct users, highest first. */
  versions: GpcVitalsVersionRow[];
}

/**
 * Apple's source types, in the table's column order (A7Y-5740): the
 * `source_type` values App Store Connect writes on every row of the App
 * Downloads and Discovery and Engagement reports.
 * source: production `asc_analytics.commerce` / `app_store_engagement`, distinct `source_type`, read 2026-10-21.
 */
export const ASC_SOURCE_TYPES = [
  "App Store search",
  "App Store browse",
  "Web referrer",
  "App referrer",
  "Unavailable",
] as const;

/** Column headings for `ASC_SOURCE_TYPES`; any other value is shown as Apple writes it. */
export const ASC_SOURCE_TYPE_LABELS: Record<string, string> = {
  "App Store search": "App Store Search",
  "App Store browse": "App Store Browse",
  "Web referrer": "Web Referrer",
  "App referrer": "App Referrer",
  Unavailable: "Unavailable",
};

/** One territory × source type cell, or a total (A7Y-5740). */
export interface AscSourceTerritoryCell {
  firstTimeDownloads: number;
  pageViews: number;
  /** First-time downloads over page views, 0..n; null when there were no page views. */
  conversionRate: number | null;
}

/** One row of the source × territory table: a territory, or all territories. */
export interface AscSourceTerritoryRow {
  /** ISO 3166-1 alpha-2 code as Apple writes it; "Total" on the total row. */
  territory: string;
  /** One cell per entry of `sourceTypes`, in that order. */
  cells: AscSourceTerritoryCell[];
  /** The row across every source type. */
  total: AscSourceTerritoryCell;
}

/** GET /analytics/source-territory (A7Y-5740) */
export interface AscSourceTerritoryResponse {
  platform: "asc";
  dateRange: AnalyticsDateRange;
  /**
   * The columns: every entry of `ASC_SOURCE_TYPES`, then any other source
   * type present in the range, so the total column is the sum of the columns.
   */
  sourceTypes: string[];
  /** All territories summed. */
  total: AscSourceTerritoryRow;
  /** Ordered by first-time downloads, then page views, highest first. */
  territories: AscSourceTerritoryRow[];
}

/** GET /analytics/funnel */
export interface AnalyticsFunnelResponse {
  dateRange: AnalyticsDateRange;
  asc?: {
    stages: Array<{
      name: string;
      value: number;
      delta: number;
      conversionToNext: number | null;
    }>;
    timeseries: Array<Record<string, number | string>>;
  };
  gpc?: {
    stages: Array<{
      name: string;
      value: number;
      delta: number;
      conversionToNext: number | null;
    }>;
    timeseries: Array<Record<string, number | string>>;
  };
}

// ======================================================================================|
// MARK: Annotations

/** A vertical annotation marker on time-series charts (e.g. published revision). */
export interface AnalyticsAnnotation {
  /** Date the event occurred (YYYY-MM-DD) */
  date: string;
  /** Short label rendered on the chart (e.g. "Rev #3") */
  label: string;
  /** Revision number that was published */
  revisionNumber: number;
  /**
   * A7Y-1946: measured KPI lift for this published revision. Present once the
   * attribution artifact exists (A7Y-1991: the summary now carries
   * `headlineWindowStatus` = captured/pending/skipped, so it is set even before
   * the 7-day window resolves). Absent only when no artifact exists for the revision.
   */
  measuredLift?: RevisionAttributionSummary;
}

// ======================================================================================|
// MARK: Before/After Proof Panel (A7Y-1813)

/**
 * A single before/after metric pair plus its percentage delta.
 * `delta` follows the same convention as `calculateDelta` in the query helpers:
 * percentage change of `after` relative to `before`.
 */
export interface BeforeAfterDelta {
  before: number;
  after: number;
  delta: number;
}

/**
 * One row of a before/after split (e.g. branded vs non-branded, new vs
 * returning, or per traffic-source). `available` is set honestly per console:
 * when a console does not expose the dimension needed for the split, `available`
 * is `false` and `reason` explains why so the panel can degrade truthfully.
 */
export interface BeforeAfterSplit {
  /** Stable machine key for the split row (e.g. "branded", "new", "search"). */
  key: string;
  /** Human-readable label for the split row. */
  label: string;
  /** Whether this console exposes the data needed to compute this split. */
  available: boolean;
  /** When `available` is false (or low-confidence), why. */
  reason?: string;
  /** Before/after values + delta for this split row. */
  delta: BeforeAfterDelta;
}

/**
 * GET /analytics/before-after — window-delta proof panel comparing N weeks
 * before vs N weeks after a pinned listing-change date.
 */
export interface AnalyticsBeforeAfterResponse {
  platform: AnalyticsPlatform;
  /** Pinned listing-change date (YYYY-MM-DD); the boundary between windows. */
  pinnedDate: string;
  /** Number of weeks in each window (clamped 1–12). */
  weeks: number;
  /** The "before" comparison window (ends the day before pinnedDate). */
  beforeRange: AnalyticsDateRange;
  /** The "after" window (starts on pinnedDate). */
  afterRange: AnalyticsDateRange;
  /** Headline discoverability/conversion metrics, before vs after. */
  headline: {
    /** ASC impressions / GPC store-listing visitors, before vs after. */
    impressionsOrVisitors: BeforeAfterDelta;
    /** Product page views (ASC) / store-listing visitors detail (GPC). */
    productPageViews: BeforeAfterDelta;
    /** Conversion rate (downloads or acquisitions ÷ views/visitors). */
    conversionRate: BeforeAfterDelta;
  };
  /** Branded vs non-branded traffic split. */
  brandedSplit: BeforeAfterSplit[];
  /** New vs returning install/download split. */
  newReturningSplit: BeforeAfterSplit[];
  /** Traffic-source breakdown split. */
  sourceSplit: BeforeAfterSplit[];
  /**
   * AI-discovery score before/after — EXTENSION SEAM for A7Y-1814. Declared
   * optional here and intentionally left unpopulated by the backend until that
   * ticket wires it in.
   */
  aiDiscoveryScore?: BeforeAfterDelta;
}

/** GET /analytics/cumulative */
export interface AnalyticsCumulativeResponse {
  dateRange: AnalyticsDateRange;
  asc?: {
    daily: AnalyticsTimeSeries[];
    cumulative: AnalyticsTimeSeries[];
    previousDaily: AnalyticsTimeSeries[];
    previousCumulative: AnalyticsTimeSeries[];
    total: number;
    previousTotal: number;
    delta: number;
  };
  gpc?: {
    daily: AnalyticsTimeSeries[];
    cumulative: AnalyticsTimeSeries[];
    previousDaily: AnalyticsTimeSeries[];
    previousCumulative: AnalyticsTimeSeries[];
    total: number;
    previousTotal: number;
    delta: number;
  };
}
